import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from 'ui-box';
import * as Yup from 'yup';
import { Formik, Form } from 'formik';
import { Typography, Button, Divider } from 'antd';
import InputSelectField from '../../../components/mobile/form-helpers/InputSelectField';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import HeaderBack from '../../../components/mobile/speak-doctor/HeaderBack';
import { ReactComponent as UploadSvg } from '../../../assets/icons/upload.svg';
import { getPhysicianAPICall, postSendDoctorAttachmentAPICall } from '../../../api/patient';
import { errorNotification, successNotification } from '../../../utils/notification-helpers';

const { Title } = Typography;

const formItemLayout = {
  labelCol: {
    span: 24,
  },
  wrapperCol: {
    span: 24,
  },
}

const types = [
  {label: "Prescription / Drugs", value: "Prescription / Drugs"},
  {label: "Lab Report", value: "Lab Report"},
  {label: "Medical Record", value: "Medical Record"},
  {label: "Picture of health issue", value: "Picture of health issue"},
  {label: "Other", value: "Other"},
]

const styles = {
  boxUpload: {
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
    justifyContent: "center",
    textAlign: "center",
    padding: "30px 16px",
    background: "#F5F5F5",
    border: "1px dashed #D9D9D9",
    borderRadius: "2px",
    cursor: "pointer"
  },
  label: {
    fontWeight: "400",
    fontSize: "14px",
    lineHeight: "28px",
    color: "#262626",
    marginBottom: 8
  },
  uploadTitle: {
    fontWeight: "400",
    fontSize: "16px",
    lineHeight: "24px",
    color: "#262626",
    marginTop: 30,
    marginBottom: 8
  },
  uploadDesc: {
    fontWeight: "400",
    fontSize: "14px",
    lineHeight: "24px",
    color: "#8C8C8C"
  }
}

function MobileSendDoctorScreen() {
  const { t } = useTranslation(['speak-doctor', 'common']);
  const [phisicians, setPhisicians] = useState([])
  const [dataFile, setDataFile] = useState(null)
  const inputFile = useRef(null)
  const history = useHistory();

  const FormSchema = useMemo(
    () =>
      Yup.object().shape({
        physician_guid: Yup.string().required(t('common:form.error.required')),
        type: Yup.string().required(t('common:form.error.required'))
      }),
    [t]
  );

  const initialValues = {
    physician_guid: '',
    type: '',
  };

  const fetchPhysician = async () => {
    const { responseOk, responseJSON } = await getPhysicianAPICall()
    if (responseOk) {
      const newData = responseJSON.map((el) => {
        const item = {};
        item.label = el.name;
        item.value = el.guid;
        return item;
      });
      setPhisicians(newData)
    }
  }

  useEffect(() => {
    fetchPhysician();
  }, []);

  const onChangeFile = (event) => {
    event.stopPropagation();
    event.preventDefault();
    setDataFile(event.target.files[0])
}

  return (
    <>
      <HeaderBack
        previousPage='/homepage'
        text={t('speak-doctor:sendDoctor.returnHome')}
        title={t('speak-doctor:sendDoctor.title')}
        subtitle={t('speak-doctor:sendDoctor.subtitle')}
      />
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={FormSchema}
        onSubmit={async (values, { setSubmitting, resetForm }) => {
          const dataForm = { ...values, file: dataFile };

          try {
            const { responseOk, responseJSON } = await postSendDoctorAttachmentAPICall(dataForm);
            setSubmitting(false);
            setDataFile(null)
            resetForm({ values: initialValues });
            if (!responseOk) {
              throw new Error(`${responseOk} - ${responseJSON}`);
            }
            successNotification(t('common:form.success.submit'));
            history.push(`/homepage`);
          } catch (err) {
            setDataFile(null)
            errorNotification(t('common:form.error.submit'));
            setSubmitting(false);
            resetForm({ values: initialValues });
          }
        }}
      >
        {({ isSubmitting, isValid }) => {
          return (
            <Form>
              <fieldset disabled={isSubmitting}>
                <InputSelectField
                  name="physician_guid"
                  style={{ width: '100%', padding: '3%' }}
                  formItemLayout={formItemLayout}
                  options={phisicians}
                />
                <Divider/>
                <InputSelectField
                  name="type"
                  label={t('speak-doctor:sendDoctor.whatFile')}
                  style={{ width: '100%', padding: '3%' }}
                  formItemLayout={formItemLayout}
                  options={types}
                />
                <input
                  type='file'
                  id='file'
                  ref={inputFile}
                  style={{display: 'none'}}
                  onChange={onChangeFile}
                />
                <Box onClick={() => inputFile.current.click()}>
                  {
                    dataFile == null
                    ? (
                      <>
                      <Title level={4} style={styles.label}>
                        {t('speak-doctor:sendDoctor.uploadAttatchment')}
                      </Title>
                      <Box style={styles.boxUpload}>
                        <UploadSvg />
                        <Title level={4} style={styles.uploadTitle}>
                          {t('speak-doctor:sendDoctor.upload')}
                        </Title>
                        <Title level={4} style={styles.uploadDesc}>
                          {t('speak-doctor:sendDoctor.allowed')}
                        </Title>
                      </Box>
                      </>
                    )
                    : (
                      <Title level={4} style={styles.uploadDesc}>
                        {dataFile?.name}
                      </Title>
                    )
                  }
                </Box>
                <Box paddingTop={15}>
                  <Button
                    disabled={isSubmitting || !isValid || dataFile === null}
                    loading={isSubmitting}
                    htmlType="submit"
                    type="primary"
                    size="large"
                    block
                  >
                    {isSubmitting ? <LoadingSpinner position="full" transparent={false} /> : t('common:button.submit')}
                  </Button>
                </Box>
              </fieldset>
            </Form>
          );
        }}
      </Formik>
    </>
  );
}

export default MobileSendDoctorScreen;
